import { designTokens } from "@itandi/itandi-bb-ui";
import { Alert } from "@itandi/itandi-bb-ui/Alert";
import { Divider } from "@itandi/itandi-bb-ui/Divider";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { Grid } from "@itandi/itandi-bb-ui/Grid";
import { PresentationChip } from "@itandi/itandi-bb-ui/PresentationChip";
import Link from "next/link";
import { useRouter } from "next/router";
import type { ComponentProps } from "react";
import React from "react";
import { ConstructionConfirmedFooter } from "~/components/common/constructions/ConstructionConfirmedFooter";
import { ConstructionList } from "~/components/common/constructions/list/ConstructionList";
import { ConstructionDownloadButtons } from "~/components/common/constructions/top/ConstructionDownloadButtons";
import { ConstructionTop } from "~/components/common/constructions/top/ConstructionTop";
import { VendorDealLayout } from "~/components/common/layouts/deals/VendorDealLayout";
import { VendorLayoutV2 } from "~/components/common/layouts/VendorLayoutV2";
import Loading from "~/components/common/parts/loading/Loading";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import { MEDIA_QUERY, useMediaQuery } from "~/hooks/useMediaQuery";
import { useDealEstimationHeaders } from "~/hooks/vendors/deals/useDealEstimationHeaders";
import { useDealEstimations } from "~/hooks/vendors/deals/useDealEstimations";
import { useDeals } from "~/hooks/vendors/useDeals";
import { useSessions } from "~/hooks/vendors/useSessions";
import type { NskResponse } from "~/models/Api";
import { ESTIMATION_BY, estimationHeaderStatus } from "~/models/Deal";
import { estimationRequestSide } from "~/models/DealEstimationHeader";
import { isOriginVendor } from "~/models/DealVendorCompany";
import { IN_ESTIMATION, isOwnStatus } from "~/models/Status";
import { PATH as DEALS_PATH } from "~/pages/vendors/deals";
import { PATH } from "~/pages/vendors/deals/[id]";
import { PATH as ESTIMATIONS_EDIT_PATH } from "~/pages/vendors/deals/[id]/estimations/edit";
import { getDownloadPath } from "~/utils/api/vendors/deals/DealEstimationFile";
import {
	updateAccept,
	updateReject
} from "~/utils/api/vendors/deals/DealEstimationHeader";
import { getDownloadPath as getHistoryDownloadPath } from "~/utils/api/vendors/deals/DealEstimationHistoryFile";
import Styles from "./Page.module.scss";

export const Page: React.FC = () => {
	const { match: isSp, isLoaded: windowLoaded } = useMediaQuery(MEDIA_QUERY.sp);
	const id = useIdQueryHandler();
	const router = useRouter();
	const { data: deal, mutate: mutateDeal } = useDeals().show(id);
	const { data: session } = useSessions().index();
	const { data: estimationContext, mutate: mutateEstimations } =
		useDealEstimations().index(id);
	const { data: dealEstimationHeaders, mutate: mutateHeaders } =
		useDealEstimationHeaders().index(id);
	const requestHeader =
		dealEstimationHeaders?.find((header) => header.status === "request") ??
		null;
	const { data: estimationHistoryContext, isLoading } =
		useDealEstimationHeaders().show(id, requestHeader && requestHeader.id);
	React.useEffect(() => {
		if (deal === null) return;
		if (estimationRequestSide(deal, "vendor")) {
			router.replace(ESTIMATIONS_EDIT_PATH(deal.id)).then();
		}
	}, [deal, router]);
	if (
		deal === null ||
		isLoading ||
		estimationContext === null ||
		session === null ||
		dealEstimationHeaders === null
	) {
		return <Loading isLoading />;
	}
	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ as: Link, href: PATH(deal.id), label: "案件詳細" },
		{ label: "見積り確認" }
	] as const satisfies ComponentProps<typeof VendorDealLayout>["items"];
	const handleClickPreview = (): void => {
		const path = getDownloadPath(deal.id);
		window.open(path);
	};
	const handleClickHistoryPreview = (): void => {
		if (requestHeader === null) {
			throw new Error("requestHeader cannot be null");
		}
		const path = getHistoryDownloadPath(deal.id, requestHeader.id);
		window.open(path);
	};
	const handleMutateConstructions = async (): Promise<void> => {
		await mutateEstimations?.();
		await mutateHeaders?.();
		await mutateDeal?.();
	};
	const handleAcceptUpdateStatus = async (): Promise<NskResponse> => {
		if (requestHeader === null) {
			throw new Error("requestHeader cannot be null");
		}
		return updateAccept(deal.id, requestHeader.id);
	};
	const handleUpdateRejectStatus = async (): Promise<NskResponse> => {
		if (requestHeader === null) {
			throw new Error("requestHeader cannot be null");
		}
		return updateReject(deal.id, requestHeader.id);
	};
	const estimationStatus = estimationHeaderStatus(deal);
	if (!windowLoaded) return null;
	return (
		<VendorLayoutV2>
			<VendorDealLayout deal={deal} enableChat items={items}>
				<Flex
					justifyContent="space-between"
					pt={isSp ? designTokens["spacing/m"] : undefined}
					px={isSp ? designTokens["spacing/m"] : undefined}
				>
					<Flex alignItems="center" gap={designTokens["spacing/s"]}>
						<h1 className={Styles.EstimateTitle}>見積り確認</h1>
						{estimationStatus.label !== "" &&
							estimationStatus.variant !== null && (
								<PresentationChip variant={estimationStatus.variant}>
									{estimationStatus.label}
								</PresentationChip>
							)}
					</Flex>
					{!isSp ? (
						<Flex gap={designTokens["spacing/xs"]}>
							<ConstructionDownloadButtons
								constructionContext={estimationContext}
								dealEstimationDraftHeader={null}
								dealEstimationHistoryContext={estimationHistoryContext ?? null}
								handleClickEstimationHistoryPreview={
									requestHeader ? handleClickHistoryPreview : null
								}
								handleClickEstimationPreview={handleClickPreview}
								ownManagement={false}
							/>
						</Flex>
					) : null}
				</Flex>
				{estimationHeaderStatus(deal).description !== "" &&
					deal.statusId !== IN_ESTIMATION && (
						<Grid paddingX={isSp ? 16 : 0} width="fit-content">
							<Alert>{estimationHeaderStatus(deal).description}</Alert>
						</Grid>
					)}
				<ConstructionTop
					constructionContext={estimationContext}
					dealEstimationHistoryContext={estimationHistoryContext}
					readonly
				/>
				{isSp ? (
					<Flex
						direction="column"
						gap={designTokens["spacing/s"]}
						marginBottom={designTokens["spacing/l"]}
						marginTop={designTokens["spacing/s"]}
						marginX={designTokens["spacing/l"]}
					>
						<Divider />
						<ConstructionDownloadButtons
							constructionContext={estimationContext}
							dealEstimationDraftHeader={null}
							dealEstimationHistoryContext={estimationHistoryContext}
							handleClickEstimationHistoryPreview={
								requestHeader ? handleClickHistoryPreview : null
							}
							handleClickEstimationPreview={handleClickPreview}
							ownManagement={false}
							width="fill"
						/>
					</Flex>
				) : null}
				<div className={Styles.EstimateList}>
					<ConstructionList
						companyType="vendor"
						constructionContext={estimationHistoryContext ?? estimationContext}
						deal={deal}
						dealConstructionItems={[]}
						docType="estimation"
						handleMutateConstructions={handleMutateConstructions}
						readonly
						showEstimationConfirmedButton={
							(requestHeader &&
								!isOwnStatus(deal.statusId) &&
								deal.estimationBy === ESTIMATION_BY.COMPANY &&
								isOriginVendor(deal, session.vendorCompany.id)) ??
							false
						}
						showHighlight
					/>
					{requestHeader ? (
						<ConstructionConfirmedFooter
							companyType={
								isOriginVendor(deal, session.vendorCompany.id)
									? "vendor"
									: "partner"
							}
							deal={deal}
							dealEstimationHeader={requestHeader}
							handleMutate={handleMutateConstructions}
							handleUpdateAcceptStatus={handleAcceptUpdateStatus}
							handleUpdateRejectStatus={handleUpdateRejectStatus}
							isAdmin={session.admin}
						/>
					) : null}
				</div>
			</VendorDealLayout>
		</VendorLayoutV2>
	);
};
